import { useState } from 'react';
import type { SamplerDialogProps } from './sampler-dialog.types.ts';
import {
  NumericTextBox,
  type NumericTextBoxChangeEvent,
  type SwitchChangeEvent,
  TextBox,
} from '@progress/kendo-react-inputs';
import { updateSampler } from '../services/api.ts';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faUser } from '@fortawesome/free-solid-svg-icons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { ToggleSwitch } from '../components/input/toggle-switch.tsx';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import type { SamplerUser } from '../services/api.types.ts';
import styles from './sampler-dialog.module.scss';

export function SamplerDialog({ sampler, onApply, onClose }: SamplerDialogProps) {
  const [model, setModel] = useState<SamplerUser>({ ...sampler });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleValueChange = ({ target }: NumericTextBoxChangeEvent) => {
    const { name, value } = target;
    setModel((prev) => ({ ...prev, [name!]: value }));
  };

  const handleActiveChange = ({ value }: SwitchChangeEvent) => {
    setModel((prev) => ({ ...prev, active: value }));
  };

  const handleApply = async () => {
    if (model.weeklyWorkdays == undefined) {
      setError('Please enter a valid weekly working days.');
      return;
    }

    if (model.weeklySampleEstimate == undefined) {
      setError('Please enter a valid weekly samples estimate.');
      return;
    }

    setLoading(true);
    try {
      await updateSampler({
        samplerId: model.id,
        weeklyWorkdays: model.weeklyWorkdays,
        weeklySampleEstimate: model.weeklySampleEstimate,
        active: model.active,
      });
      onApply();
    } catch (error) {
      console.error('Error updating sampler details:', error);
      setError('Failed to save sampler details');
      setLoading(false);
    }
  };

  return (
    <Dialog
      title={
        <div className={styles.dialogTitle}>
          <FontAwesomeIcon icon={faUser} className={styles.titleIcon} />
          <span>Sampler Properties</span>
        </div>
      }
      className={styles.window}
      onClose={onClose}
    >
      <>
        {loading ? (
          <LoaderRender message={'Loading...'} />
        ) : (
          <>
            {error && (
              <div className={styles.alertBanner}>
                <FontAwesomeIcon icon={faExclamationTriangle} size={'xl'} />
                <span>{error}</span>
              </div>
            )}
            <div className={styles.formGrid}>
              <Label>Forename:</Label>
              <TextBox readOnly={true} value={model.forename} />
              <Label>Surname:</Label>
              <TextBox readOnly={true} value={model.surname} />
              <Label>Mergin Account:</Label>
              <TextBox readOnly={true} value={model.username} />
              <Label>Working Days:</Label>
              <NumericTextBox
                name={'weeklyWorkdays'}
                min={0}
                max={7}
                value={model.weeklyWorkdays}
                placeholder={'Enter the number of days the sampler works a week'}
                onChange={handleValueChange}
              />
              <Label>Daily Samples:</Label>
              <NumericTextBox
                name={'weeklySampleEstimate'}
                min={0}
                max={2000}
                step={10}
                value={model.weeklySampleEstimate}
                placeholder={'Enter the est. number of days the sampler takes a day'}
                onChange={handleValueChange}
              />
              <Label>Sampler Active:</Label>
              <ToggleSwitch checked={model.active} size={'large'} onChange={handleActiveChange} />
            </div>
          </>
        )}
      </>
      <DialogActionsBar layout={'end'}>
        <div className={styles.actionButtonGroup}>
          <Button onClick={onClose}>Close</Button>
          <Button themeColor={'primary'} onClick={handleApply} disabled={loading}>
            Confirm
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
}
